import type { RankingProfile } from "@/lib/rankings";
import {
  PLAY_REVIEW_COOLDOWN_MS,
  PLAY_REVIEW_MAX_LATER,
  PLAY_REVIEW_MIN_MATCHES,
  type PlayReviewState,
} from "@/lib/playReviewPrompt";

export type NotificationSuggestionKey =
  | "suggestion:primary_club"
  | "suggestion:elo_visibility"
  | "suggestion:play_review"
  | "suggestion:profile_completion"
  | `suggestion:opponent_duplicates:${string}`
  | `suggestion:club_review:${string}`
  | `suggestion:club_duplicates:${string}`;

export function shouldSuggestPrimaryClub(
  matches: number,
  clubs: ReadonlyArray<{ is_primary: boolean }> | null,
): boolean {
  // Null means the club RPC failed or was not loaded; never infer absence.
  return matches > 0 && clubs !== null && !clubs.some((club) => club.is_primary);
}

export function shouldSuggestEloVisibility(
  profile: Pick<RankingProfile, "visible" | "matches_played"> | null,
): boolean {
  return profile !== null && profile.matches_played > 0 && profile.visible === false;
}

export function shouldSuggestPlayReview(
  matches: number,
  isAndroidInstalled: boolean,
  state: PlayReviewState | null,
  now = Date.now(),
): boolean {
  // Match the Dashboard's 30-day cooldown, including when a player chose
  // "Later". The notification bell must not bypass an existing prompt opt-out.
  // A Play link click is NOT proof that a review was submitted.
  return isAndroidInstalled &&
    Number.isFinite(matches) && matches >= PLAY_REVIEW_MIN_MATCHES &&
    state !== null &&
    state.clickedAt === 0 &&
    state.laterCount < PLAY_REVIEW_MAX_LATER &&
    (state.lastShownAt <= 0 || now - state.lastShownAt >= PLAY_REVIEW_COOLDOWN_MS);
}

export function isSuggestionUnread(
  state: { read_at: string | null; dismissed_at: string | null } | undefined,
): boolean {
  return !state?.read_at && !state?.dismissed_at;
}
